import React, { useState } from 'react';
import { Box, VStack, Text, Button, HStack, AbsoluteCenter, Stack } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { ScannedQRData } from '@/types/payment';
import { useBalance } from '@/hooks/wallet/useBalance';

// アニメーション定義
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const ping = keyframes`
  0% { transform: scale(0.8); opacity: 0.8; }
  50% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.8); opacity: 0.8; }
`;

const scaleIn = keyframes`
  0% { transform: scale(0) rotate(0deg); opacity: 0; }
  50% { transform: scale(1.3) rotate(180deg); opacity: 1; }
  100% { transform: scale(1) rotate(360deg); opacity: 1; }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(56, 178, 172, 0.7); }
  70% { box-shadow: 0 0 0 30px rgba(56, 178, 172, 0); }
  100% { box-shadow: 0 0 0 0 rgba(56, 178, 172, 0); }
`;

interface PaymentConfirmContentProps {
  qrData: ScannedQRData;
  amount: number;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  onBack?: () => void;
  canGoBack?: boolean;
}

export const PaymentConfirmContent: React.FC<PaymentConfirmContentProps> = ({
  qrData,
  amount,
  onConfirm,
  onCancel,
  onBack,
  canGoBack = false,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { data: balanceData } = useBalance();
  const balance = balanceData?.balance ?? 0;
  const isInsufficientBalance = amount > balance;

  const handleConfirm = async () => {
    if (isInsufficientBalance) return;

    setIsProcessing(true);
    try {
      // 支払い処理を実行
      await onConfirm();

      // 成功アニメーションを表示
      setShowSuccess(true);
    } catch (error) {
      console.error('Payment confirmation error:', error);
      setIsProcessing(false);
      setShowSuccess(false);
    }
  };

  return (
    <Box position="relative" w="full" userSelect="none">
      <VStack gap={6} w="full" p={4} opacity={isProcessing ? 0.4 : 1} transition="opacity 0.3s">
        {/* 支払先情報 */}
        <Box w="full" bg="teal.50" borderRadius="lg" p={4}>
          <Text fontSize="sm" color="gray.600" mb={1}>
            支払先
          </Text>
          <Text fontSize="lg" fontWeight="bold" color="gray.800">
            {qrData.payeeName}
          </Text>
          {qrData.payeeDetail && (
            <Text fontSize="sm" color="gray.600" mt={1}>
              {qrData.payeeDetail}
            </Text>
          )}
        </Box>

        {/* 支払金額 */}
        <Box w="full" borderWidth="2px" borderColor="teal.200" borderRadius="lg" p={6} bg="white">
          <Text fontSize="sm" color="gray.600" mb={2} textAlign="center">
            支払金額
          </Text>
          <Text fontSize="4xl" fontWeight="bold" color="teal.600" textAlign="center">
            ฿{amount.toLocaleString()}
          </Text>
        </Box>

        {/* 残高情報 */}
        <Box w="full" bg="gray.50" borderRadius="lg" p={4}>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" color="gray.600">
              現在の残高
            </Text>
            <Text fontSize="lg" fontWeight="semibold" color="gray.800">
              ฿{balance.toLocaleString()}
            </Text>
          </HStack>
          <HStack justify="space-between">
            <Text fontSize="sm" color="gray.600">
              支払後の残高
            </Text>
            <Text
              fontSize="lg"
              fontWeight="semibold"
              color={isInsufficientBalance ? 'red.600' : 'teal.600'}
            >
              ฿{(balance - amount).toLocaleString()}
            </Text>
          </HStack>
        </Box>

        {/* 残高不足の警告 */}
        {isInsufficientBalance && (
          <Box w="full" bg="red.50" borderRadius="lg" p={4} borderWidth="1px" borderColor="red.200">
            <HStack gap={2}>
              <Text fontSize="lg">⚠️</Text>
              <Text fontSize="sm" color="red.700" fontWeight="semibold">
                残高が不足しています
              </Text>
            </HStack>
          </Box>
        )}

        {/* アクションボタン */}
        <VStack gap={2} w="full" mt={4}>
          <Button
            w="full"
            size="lg"
            colorPalette="teal"
            onClick={handleConfirm}
            disabled={isInsufficientBalance || isProcessing}
            loading={isProcessing}
          >
            {isInsufficientBalance ? '残高が不足しています' : '支払いを確定する'}
          </Button>
          {canGoBack && onBack && (
            <Button w="full" size="lg" variant="outline" onClick={onBack} disabled={isProcessing}>
              戻る
            </Button>
          )}
          <Button w="full" size="lg" variant="ghost" onClick={onCancel} disabled={isProcessing}>
            キャンセル
          </Button>
        </VStack>

        {/* 注意事項 */}
        <Box w="full" bg="gray.50" borderRadius="lg" p={3}>
          <Text fontSize="xs" color="gray.600" textAlign="center">
            支払いを確定すると、即座に残高から引き落とされます
          </Text>
        </Box>
      </VStack>

      {/* 処理中・成功オーバーレイ */}
      {isProcessing && (
        <AbsoluteCenter>
          <VStack gap={6}>
            {showSuccess ? (
              // 成功アニメーション
              <VStack gap={4}>
                <Box
                  css={{
                    animation: `${scaleIn} 0.6s ease-out forwards`,
                  }}
                >
                  <Box
                    w="120px"
                    h="120px"
                    borderRadius="full"
                    bg="teal.500"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    css={{
                      animation: `${pulse} 1.2s infinite`,
                    }}
                  >
                    <Text fontSize="6xl" color="white" fontWeight="bold">
                      ✓
                    </Text>
                  </Box>
                </Box>
                <VStack gap={2}>
                  <Text fontSize="2xl" fontWeight="bold" color="teal.600">
                    支払い完了！
                  </Text>
                  <Text fontSize="md" color="gray.600">
                    お支払いが正常に完了しました
                  </Text>
                </VStack>
              </VStack>
            ) : (
              // ローディングスピナー
              <Stack gap={5} align="center">
                <Box position="relative">
                  <Box
                    w="100px"
                    h="100px"
                    borderRadius="full"
                    borderWidth="6px"
                    borderColor="teal.100"
                    borderTopColor="teal.500"
                    css={{
                      animation: `${spin} 1s linear infinite`,
                    }}
                  />
                  <AbsoluteCenter>
                    <Box
                      w="60px"
                      h="60px"
                      borderRadius="full"
                      bg="teal.500"
                      css={{
                        animation: `${ping} 1.5s cubic-bezier(0, 0, 0.2, 1) infinite`,
                      }}
                    />
                  </AbsoluteCenter>
                </Box>
                <VStack gap={1}>
                  <Text fontSize="xl" fontWeight="bold" color="teal.700">
                    決済処理中
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    しばらくお待ちください...
                  </Text>
                </VStack>
              </Stack>
            )}
          </VStack>
        </AbsoluteCenter>
      )}
    </Box>
  );
};
